import { useEffect, useState } from 'react'
import { Row, Col, Card, Statistic, Table, Typography, Tag } from 'antd'
import { ShoppingCartOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons'
import { getBalance } from '../../services/balance'
import { getOrders } from '../../services/order'
import { formatTimestamp } from '../../utils/format'
import { formatAmount, formatDisplayExchangeRate } from '../../utils/orderDisplay'
import { orderDisplayStatus } from '../../utils/orderDisplayStatus'
import type { Balance, Order } from '../../types'

const { Title } = Typography

export default function Dashboard() {
  const [balance, setBalance] = useState<Balance | null>(null)
  const [recentOrders, setRecentOrders] = useState<Order[]>([])
  const [todayOrderCount, setTodayOrderCount] = useState(0)
  const [todaySuccessfulOrderCount, setTodaySuccessfulOrderCount] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const todayStart = new Date()
    todayStart.setHours(0, 0, 0, 0)

    const tomorrowStart = new Date(todayStart)
    tomorrowStart.setDate(tomorrowStart.getDate() + 1)
    const todayOrderParams = {
      page: 1,
      size: 1,
      startDate: todayStart.getTime(),
      endDate: tomorrowStart.getTime() - 1
    }

    Promise.all([
      getBalance(),
      getOrders({ page: 1, size: 5 }),
      getOrders(todayOrderParams),
      getOrders({ ...todayOrderParams, status: 'PAID' }),
      getOrders({ ...todayOrderParams, status: 'SETTLED' })
    ]).then(([balanceRes, ordersRes, todayOrdersRes, todayPaidOrdersRes, todaySettledOrdersRes]) => {
      setBalance(balanceRes.data)
      setRecentOrders(ordersRes.data.items)
      setTodayOrderCount(todayOrdersRes.data.pagination.total)
      setTodaySuccessfulOrderCount(
        todayPaidOrdersRes.data.pagination.total + todaySettledOrdersRes.data.pagination.total
      )
    }).finally(() => {
      setLoading(false)
    })
  }, [])

  const computeFee = (record: Order) =>
    record.platformFeeUsd
      ? `${formatAmount(record.platformFeeUsd)} USD`
      : `${formatAmount(record.platformFee)} ${record.currency}`

  const columns = [
    { title: '訂單號', dataIndex: 'id', key: 'id', width: 200 },
    {
      title: '客戶郵箱',
      dataIndex: 'customerEmail',
      key: 'customerEmail',
      render: (email: string) => email || '-'
    },
    {
      title: '訂單金額',
      key: 'amount',
      render: (_: unknown, record: Order) => `${formatAmount(record.amount)} ${record.currency}`
    },
    {
      title: '匯率',
      key: 'exchangeRate',
      render: (_: unknown, record: Order) =>
        formatDisplayExchangeRate(record.currency, record.exchangeRate)
    },
    {
      title: '手續費',
      key: 'fee',
      render: (_: unknown, record: Order) => computeFee(record)
    },
    {
      title: '實際結算金額',
      key: 'settledAmount',
      render: (_: unknown, record: Order) =>
        record.settledAmount
          ? `${formatAmount(record.settledAmount)} USD`
          : '-'
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (_: string, order: Order) => {
        const displayStatus = orderDisplayStatus(order.status, order.refundStatus)
        const colorMap: Record<string, string> = { PENDING: 'gold', PAID: 'green', SETTLED: 'blue', REFUNDED: 'orange', DISPUTED: 'red' }
        const labelMap: Record<string, string> = { PENDING: '待支付', PAID: '已支付', SETTLED: '已結算', REFUNDED: '已退款', DISPUTED: '爭議中' }
        return <Tag color={colorMap[displayStatus]}>{labelMap[displayStatus] || displayStatus}</Tag>
      }
    },
    {
      title: '時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (ts: number) => formatTimestamp(ts)
    },
  ]

  return (
    <div className="dashboard-page">
      <Title level={4} className="page-title">儀表盤</Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="可用餘額"
              value={balance ? formatAmount(balance.available) : 0}
              prefix="$"
              suffix="USD"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="待結算"
              value={balance ? formatAmount(balance.pending) : 0}
              prefix={<ClockCircleOutlined />}
              suffix="USD"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="今日訂單"
              value={todayOrderCount}
              prefix={<ShoppingCartOutlined />}
              suffix="筆"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="今日成功訂單"
              value={todaySuccessfulOrderCount}
              prefix={<CheckCircleOutlined />}
              suffix="筆"
            />
          </Card>
        </Col>
      </Row>

      <Card title="最近訂單">
        <Table
          columns={columns}
          dataSource={recentOrders}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>
    </div>
  )
}
